import { useRef, useState } from "react";
import { TAP_RESET_MS, addTap, tapTempo } from "../../learning/tempo";
import { keyName, keyTonics, transposeKey, type Mode } from "../../theory/key";
import { MAX_BPM, MIN_BPM } from "../../sequencer/time";
import { useDirty, useHistoryState, useLive, usePosition, useProject, useServices } from "../context";
import { formatPosition, positionFromTicks } from "../../sequencer/time";
import { Select, Toggle } from "./controls";

const AUDIO_LABEL = { "not-started": "Audio off", starting: "Starting…", running: "Audio on", suspended: "Audio paused", error: "Audio error" } as const;

export interface TopBarProps {
  theoryOpen: boolean;
  onToggleTheory: () => void;
}

/**
 * Desktop: one wrapping bar with every control. Phones: a primary row (audio,
 * transport, Song ▾, Theory, Panic) with song/project details behind Song ▾.
 */
export function TopBar({ theoryOpen, onToggleTheory }: TopBarProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { controller, library } = useServices();
  const song = useProject((p) => p.song);
  const name = useProject((p) => p.name);
  const metronome = useProject((p) => p.metronome);
  const audio = useLive((s) => s.audio);
  const playing = useLive((s) => s.playing);
  const countIn = useLive((s) => s.countIn);
  const midi = useLive((s) => s.midi.state);
  const dropped = useLive((s) => s.droppedNotes);
  const dirty = useDirty();
  const history = useHistoryState();

  const setSong = (patch: Partial<typeof song>) => controller.updateProject((p) => ({ ...p, song: { ...p.song, ...patch } }));
  const perfKey = transposeKey(song.key, song.transposeSemitones);
  const t = song.transposeSemitones;

  return (
    <header className="topbar" role="banner">
      <div className="topbar-group topbar-primary">
        {audio !== "running" ? (
          <button type="button" className="primary" aria-label={audio === "starting" ? "Starting audio" : "Start audio"} onClick={() => void controller.startAudio()}>
            <span aria-hidden="true">{audio === "starting" ? "Starting" : "Start"}<span className="hide-phone"> audio</span>{audio === "starting" ? "…" : ""}</span>
          </button>
        ) : (
          <span className="status ok" role="status">{AUDIO_LABEL[audio]}</span>
        )}
        <button type="button" className={`transport ${playing ? "playing" : ""}`} aria-label={playing ? "Stop" : "Play"} onClick={() => controller.togglePlay()} title="Play / stop (Space)">
          <span aria-hidden="true">{playing ? "■" : "▶"}</span><span className="hide-phone" aria-hidden="true">{playing ? " Stop" : " Play"}</span>
        </button>
        {countIn !== null ? <span className="status warn count-in" role="status" aria-live="assertive">Count-in {countIn}</span> : <PositionReadout />}
        <button type="button" className="mobile-only" aria-expanded={detailsOpen} aria-controls="topbar-details" onClick={() => setDetailsOpen((o) => !o)}>
          Song {detailsOpen ? "▴" : "▾"}{dirty ? " •" : ""}
        </button>
        <button type="button" className="mobile-only" aria-pressed={theoryOpen} onClick={onToggleTheory}>Theory</button>
      </div>

      <div id="topbar-details" className={`topbar-details ${detailsOpen ? "open" : ""}`}>
      <div className="topbar-group" aria-label="Tempo and metronome">
        <label className="inline-field">
          <span>Tempo</span>
          <input
            type="number" min={MIN_BPM} max={MAX_BPM} value={song.tempoBpm} aria-label="Tempo in BPM"
            onChange={(e) => {
              const v = Number(e.currentTarget.value);
              if (Number.isFinite(v) && v >= MIN_BPM && v <= MAX_BPM) setSong({ tempoBpm: Math.round(v) });
            }}
          />
          <span className="unit">BPM</span>
        </label>
        <TapTempo onTempo={(bpm) => setSong({ tempoBpm: bpm })} />
        <Select
          label="Beats per bar" value={song.timeSignature.beatsPerBar}
          options={[2, 3, 4, 5, 6, 7].map((n) => ({ value: n, label: `${n}/4` }))}
          onChange={(n) => controller.updateProject((p) => ({ ...p, song: { ...p.song, timeSignature: { beatsPerBar: n, beatUnit: 4 } } }))}
        />
        <Toggle pressed={metronome.enabled} onChange={(v) => controller.updateProject((p) => ({ ...p, metronome: { ...p.metronome, enabled: v } }))} title="Metronome click (monitor bus)">
          Click
        </Toggle>
        <Select
          label="Count-in" value={metronome.countInBars}
          options={[0, 1, 2].map((n) => ({ value: n, label: n === 0 ? "Off" : `${n} bar${n > 1 ? "s" : ""}` }))}
          onChange={(countInBars) => controller.updateProject((p) => ({ ...p, metronome: { ...p.metronome, countInBars } }))}
        />
      </div>

      <div className="topbar-group" aria-label="Key and transposition">
        <Select label="Key" value={song.key.tonic} options={keyTonics(song.key.mode).map((k) => ({ value: k, label: k }))} onChange={(tonic) => setSong({ key: { ...song.key, tonic } })} />
        <Select
          label="Mode" hideLabel value={song.key.mode} options={[{ value: "major", label: "major" }, { value: "minor", label: "minor" }]}
          onChange={(mode: Mode) => setSong({ key: { tonic: keyTonics(mode).includes(song.key.tonic) ? song.key.tonic : keyTonics(mode)[0]!, mode } })}
        />
        <div className="transpose" role="group" aria-label="Song transposition">
          <button type="button" aria-label="Transpose down a semitone" disabled={t <= -12} onClick={() => setSong({ transposeSemitones: t - 1 })}>−</button>
          <span className="transpose-readout" aria-live="polite">
            {t === 0 ? <>Performing in <strong>{keyName(song.key)}</strong></> : <>{keyName(song.key)} → <strong>{keyName(perfKey)}</strong> ({t > 0 ? "+" : ""}{t})</>}
          </span>
          <button type="button" aria-label="Transpose up a semitone" disabled={t >= 12} onClick={() => setSong({ transposeSemitones: t + 1 })}>+</button>
        </div>
        {dropped > 0 && <span className="status warn" title="Transposed notes outside MIDI range 0–127 were skipped">{dropped} notes out of range</span>}
      </div>

      <div className="topbar-group" aria-label="Project">
        <span className={`status ${midi === "ready" ? "ok" : "muted"}`} title="MIDI status (see My Gear)">MIDI: {midi === "ready" ? "on" : midi}</span>
        <span className="project-name" title={name}>{name}{dirty ? " •" : ""}<span className="sr-only">{dirty ? " (unsaved changes)" : ""}</span></span>
        <button type="button" onClick={() => controller.undo()} disabled={!history.canUndo} aria-keyshortcuts="Control+Z Meta+Z" title="Undo (Ctrl/Cmd+Z)">Undo</button>
        <button type="button" onClick={() => controller.redo()} disabled={!history.canRedo} aria-keyshortcuts="Control+Y Control+Shift+Z Meta+Shift+Z" title="Redo (Ctrl+Y / Ctrl/Cmd+Shift+Z)">Redo</button>
        <button type="button" onClick={() => void library.save()} disabled={!dirty} title="Save project locally">Save</button>
      </div>
      </div>

      <button type="button" className="panic" onClick={() => controller.panic()} title="Stop all sound immediately (Esc)">Panic</button>
    </header>
  );
}

/**
 * Bar.beat while playing. A separate component so only this re-renders on
 * every step. Not a live region: announcing every beat would be noise.
 */
function PositionReadout() {
  const pos = usePosition();
  const ts = useProject((p) => p.song.timeSignature);
  if (pos === null) return null;
  const text = formatPosition(positionFromTicks(pos, ts));
  return <span className="position-readout" title="Loop position (bar.beat)" aria-label={`Bar and beat ${text}`}>{text}</span>;
}

/**
 * Tap tempo: tap ≥ 3 times; each burst of taps is one undo step. Uses
 * pointerdown (not click) so the tap time is when the finger lands.
 */
function TapTempo({ onTempo }: { onTempo: (bpm: number) => void }) {
  const { controller } = useServices();
  const taps = useRef<number[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [count, setCount] = useState(0);
  const tap = () => {
    const now = performance.now();
    if (taps.current.length === 0 || now - taps.current[taps.current.length - 1]! > TAP_RESET_MS) controller.beginEditGroup();
    taps.current = addTap(taps.current, now);
    setCount(taps.current.length);
    const bpm = tapTempo(taps.current);
    if (bpm !== null) onTempo(bpm);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { taps.current = []; setCount(0); controller.endEditGroup(); }, TAP_RESET_MS);
  };
  return (
    <button type="button" className={count > 0 ? "tapping" : ""} title="Tap at least three times in time with the music"
      onPointerDown={(e) => { if (e.pointerType !== "mouse" || e.button === 0) tap(); }}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !e.repeat) { e.preventDefault(); tap(); } }}>
      Tap{count > 0 && count < 3 ? ` (${3 - count})` : ""}
    </button>
  );
}
